import { Component } from '@theme/component';

/**
 * Sidebar panel switching for hybrid mega menu.
 *
 * @extends {Component}
 */
class MegaMenuHybrid extends Component {
  /** @type {HTMLElement | null} */
  #sidebar = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onSidebarClick);
    this.#sidebar = this.querySelector('.mega-menu-hybrid__sidebar');
    this.#sidebar?.addEventListener('pointerover', this.#onSidebarPointerOver);
    this.#injectPromos();
    this.#syncFromDataset();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onSidebarClick);
    this.#sidebar?.removeEventListener('pointerover', this.#onSidebarPointerOver);
    this.#sidebar = null;
  }

  /**
   * @param {string} handle
   */
  setActiveHandle(handle) {
    if (!handle) return;
    this.dataset.activeHandle = handle;
    this.#syncFromDataset();
    this.dispatchEvent(new CustomEvent('mega-menu-hybrid:change', { bubbles: true }));
  }

  #injectPromos() {
    const registry = this.closest('.header-mega-menu')?.querySelector('.header-mega-menu__block-registry');
    if (!registry) return;

    registry.querySelectorAll('[data-mega-menu-promo]').forEach((promoEl) => {
      if (!(promoEl instanceof HTMLElement)) return;

      const matchKey = promoEl.dataset.menuMatchKey;
      if (!matchKey) return;

      const badgeTemplate = promoEl.querySelector('[data-mega-menu-promo-badge]');
      if (badgeTemplate instanceof HTMLTemplateElement) {
        const badgeText = badgeTemplate.content.textContent?.trim();
        const trigger = this.querySelector(`[data-hybrid-sidebar-trigger="${matchKey}"]`);

        if (badgeText && trigger instanceof HTMLElement && !trigger.querySelector('.mega-menu-hybrid__badge')) {
          const badge = document.createElement('span');
          badge.className = 'mega-menu-hybrid__badge';
          badge.textContent = badgeText;
          const label = trigger.querySelector('.mega-menu-hybrid__sidebar-text');
          label?.insertAdjacentElement('afterend', badge);
        }
      }

      const featuredTemplate = promoEl.querySelector('[data-mega-menu-promo-featured]');
      if (featuredTemplate instanceof HTMLTemplateElement) {
        const columns = this.querySelector(
          `[data-hybrid-panel="${matchKey}"] .mega-menu-hybrid-panel__columns`
        );

        if (columns instanceof HTMLElement && !columns.querySelector('.mega-menu-hybrid-panel__featured')) {
          columns.appendChild(featuredTemplate.content.cloneNode(true));
        }
      }

      const primary = this.querySelector(
        `[data-hybrid-panel="${matchKey}"] .mega-menu-hybrid-panel__primary`
      );

      if (!(primary instanceof HTMLElement) || primary.querySelector('.mega-menu-promo-card')) return;

      const cardSource = promoEl.querySelector('[data-mega-menu-promo-card-source]');
      const card = cardSource?.querySelector('.mega-menu-promo-card');
      if (card instanceof HTMLElement) {
        primary.appendChild(card.cloneNode(true));
        return;
      }

      const cardTemplate = promoEl.querySelector('template[data-mega-menu-promo-card]');
      if (cardTemplate instanceof HTMLTemplateElement) {
        primary.appendChild(cardTemplate.content.cloneNode(true));
      }
    });
  }

  #syncFromDataset() {
    const handle = this.dataset.activeHandle;
    if (!handle) return;

    this.querySelectorAll('[data-hybrid-panel]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      const isActive = panel.dataset.hybridPanel === handle;
      panel.hidden = !isActive;
    });

    this.querySelectorAll('[data-hybrid-sidebar-trigger]').forEach((trigger) => {
      if (!(trigger instanceof HTMLElement)) return;
      const isActive = trigger.dataset.hybridSidebarTrigger === handle;
      trigger.classList.toggle('mega-menu-hybrid__sidebar-button--active', isActive);
      trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });
  }

  /** @param {Event} event */
  #onSidebarPointerOver = (event) => {
    const trigger = this.#getSidebarTrigger(event.target);
    if (!trigger) return;

    const handle = trigger.dataset.hybridSidebarTrigger;
    if (handle && handle !== this.dataset.activeHandle) {
      this.setActiveHandle(handle);
    }
  };

  /** @param {Event} event */
  #onSidebarClick = (event) => {
    const trigger = this.#getSidebarTrigger(event.target);
    if (!trigger) return;

    event.preventDefault();
    const handle = trigger.dataset.hybridSidebarTrigger;
    if (handle) this.setActiveHandle(handle);
  };

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null}
   */
  #getSidebarTrigger(target) {
    if (!(target instanceof Element)) return null;

    const trigger = target.closest('[data-hybrid-sidebar-trigger]');
    return trigger instanceof HTMLElement ? trigger : null;
  }
}

if (!customElements.get('mega-menu-hybrid')) {
  customElements.define('mega-menu-hybrid', MegaMenuHybrid);
}
